import { useState } from "react";
import type { FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type FormState = "idle" | "submitting" | "success" | "error";

function getFormString(data: FormData, key: string): string {
  const v = data.get(key);
  return typeof v === "string" ? v : "";
}

export function AccessRequestForm() {
  const [formState, setFormState] = useState<FormState>("idle");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormState("submitting");

    const form = e.currentTarget;
    const data = new FormData(form);
    const email = getFormString(data, "email").trim();
    const name = getFormString(data, "name").trim();
    const website = getFormString(data, "website").trim();
    const firstName = name.split(" ")[0] ?? name;

    if (website) {
      setFormState("success");
      return;
    }

    const formId = import.meta.env.VITE_LOOPS_FORM_ID ?? "";
    if (!formId) {
      console.error("VITE_LOOPS_FORM_ID is not configured");
      setFormState("error");
      return;
    }
    const body = new URLSearchParams({ email, firstName });

    try {
      const res = await fetch(
        `https://app.loops.so/api/newsletter-form/${formId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: body.toString(),
        },
      );
      if (!res.ok) throw new Error(`Loops API error: ${res.status} ${res.statusText}`);
      setFormState("success");
    } catch (err) {
      console.error("Signup form error:", err);
      setFormState("error");
    }
  }

  const isError = formState === "error";
  const errorId = "signup-error";

  if (formState === "success") {
    return (
      <div className="rounded-lg border border-success/30 bg-success/5 p-8 text-center">
        <h2 className="font-heading mb-3 text-2xl font-semibold text-foreground">
          Request received.
        </h2>
        <p className="text-sm leading-relaxed text-foreground-muted">
          We&apos;ll review your request and follow up with next steps.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground">
          Full name
          <span aria-hidden="true" className="ml-0.5 text-destructive">*</span>
          <span className="sr-only"> (required)</span>
        </label>
        <Input
          id="name"
          name="name"
          type="text"
          autoComplete="name"
          required
          placeholder="Jane Smith"
          aria-invalid={isError || undefined}
          aria-describedby={isError ? errorId : undefined}
        />
      </div>

      <div>
        <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground">
          Business email
          <span aria-hidden="true" className="ml-0.5 text-destructive">*</span>
          <span className="sr-only"> (required)</span>
        </label>
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          placeholder="you@company.com"
          aria-invalid={isError || undefined}
          aria-describedby={isError ? errorId : undefined}
        />
      </div>

      <div className="hidden" aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
      </div>

      {isError ? (
        <p id={errorId} role="alert" className="min-h-5 text-sm text-destructive">
          We couldn&apos;t send the request. Please try again in a moment.
        </p>
      ) : (
        <div className="min-h-5" aria-hidden="true" />
      )}

      <Button
        type="submit"
        disabled={formState === "submitting"}
        className="w-full"
      >
        {formState === "submitting" ? "Submitting..." : "Request access"}
      </Button>
    </form>
  );
}
